import React from 'react';
import type { Review, ReviewPhoto } from '@noths/polaris-design-system-ooux-interfaces';

import { VIEW_ALL_PHOTOS } from './copy';
import type { ReviewPreviewCardProps } from './types';
import * as styles from './ReviewPreviewCardPhotos.styles';

interface ReviewPreviewCardPhotosProps {
  href: string;
  onControlClick?: ReviewPreviewCardProps['onControlClick'];
  /** Already trimmed to what the variant shows, so this component does no slicing. */
  photos: ReviewPhoto[];
  review: Review;
  showViewAllPhotos: boolean;
}

/**
 * The photos a customer attached, as thumbnails under the review text.
 *
 * Each one is a link to the review panel rather than a lightbox of its own: the panel is
 * where a customer reads the review the photos belong to, so a photo and "Read more" go
 * to the same place. `photoIndex` is what a consumer tracks to tell them apart.
 */
export const ReviewPreviewCardPhotos = ({
  href,
  onControlClick,
  photos,
  review,
  showViewAllPhotos,
}: ReviewPreviewCardPhotosProps) => {
  if (photos.length === 0) {
    return null;
  }

  return (
    <div css={styles.photoRow} data-testid="review-preview-card-photos">
      <div css={styles.photos}>
        {photos.map((photo, index) => (
          <a
            css={styles.photoLink}
            href={href}
            /* eslint-disable-next-line react/no-array-index-key -- the index is what makes
               the key unique: a customer can attach the same photo twice, and a url-only
               key would collide. */
            key={`${photo.url}-${index}`}
            onClick={(e) => onControlClick?.(e, { control: 'photo', photoIndex: index, review })}
          >
            <img
              alt={photo.alt}
              css={styles.photo}
              decoding="async"
              loading="lazy"
              src={photo.url}
            />
          </a>
        ))}
      </div>
      {showViewAllPhotos && (
        <a
          css={styles.viewAllPhotos}
          href={href}
          onClick={(e) => onControlClick?.(e, { control: 'viewAllPhotos', review })}
        >
          {VIEW_ALL_PHOTOS}
        </a>
      )}
    </div>
  );
};

ReviewPreviewCardPhotos.displayName = 'ReviewPreviewCardPhotos';
